import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';

const storyImage = '/story-image.png';
const KEY_LOGO_URL = '/elemantra-key-logo.png';

export const StorySection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
    },
  };

  return (
    <section
      id="story"
      ref={ref}
      className="relative py-24 md:py-40 bg-[#F3EFE6] overflow-hidden"
      data-testid="story-section"
    >
      {/* Watermark Key Logo */}
      <div 
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/4 pointer-events-none"
        style={{ opacity: 0.04 }}
      >
        <img 
          src={KEY_LOGO_URL} 
          alt="" 
          className="w-[600px] h-auto object-contain"
          aria-hidden="true"
        />
      </div>

      {/* Grain Texture */}
      <div 
        className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-multiply"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center"
        >
          {/* Image */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-6 border border-[#E5DED0] -z-10" />
              <img
                src={storyImage}
                alt="Elemantra Castor Oil with packaging"
                className="w-full h-[400px] md:h-[500px] object-contain"
                data-testid="story-image"
              />
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            variants={containerVariants}
            className="lg:col-span-7 lg:pl-8"
          >
            <motion.span
              variants={itemVariants}
              className="text-xs tracking-[0.25em] uppercase text-[#B89C6A] font-outfit font-semibold"
            >
              {t.story.overline}
            </motion.span>
            
            <motion.h2
              variants={itemVariants}
              className="font-cormorant text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2A2624] mt-4 mb-10"
            >
              {t.story.title}
            </motion.h2>

            <motion.div
              variants={containerVariants}
              className="space-y-6"
            >
              <motion.p
                variants={itemVariants}
                className="font-outfit text-base leading-relaxed text-[#5C5650]"
              >
                {t.story.p1}
              </motion.p>
              <motion.p
                variants={itemVariants}
                className="font-outfit text-base leading-relaxed text-[#5C5650]"
              >
                {t.story.p2}
              </motion.p>
              <motion.p
                variants={itemVariants}
                className="font-cormorant text-xl italic text-[#2A2624]"
              >
                {t.story.p3}
              </motion.p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
